/**
 * География каталога: города Узбекистана и районы (только там, где они нужны).
 *
 * Зачем отдельный справочник, а не свободный текст в адресе: клиент выбирает «свой город»
 * из списка, и фильтр каталога обязан сверять выбор с конечным набором. Свободная строка
 * дала бы «Ташкент», «ташкент», «Tashkent» и «г. Ташкент» как четыре разных города —
 * и фильтр по городу перестал бы фильтровать.
 *
 * Хранится в `@app/shared`, а не в базе enum'ом: город у бизнеса — это `String?` в схеме,
 * проверяемый по этому списку на уровне приложения (как `tz`). Enum потребовал бы миграции
 * на каждый добавленный город; строка + сверка со списком добавляет город правкой ОДНОГО
 * этого файла.
 *
 * РАЙОНЫ — только у городов из `CITY_DISTRICTS`. Сейчас это один Ташкент: вся стратегия
 * выхода идёт по районам столицы (MARKETING.md §9), а в других городах каталог пока
 * помещается в один список. Механизм при этом общий: включить районы любого другого крупного
 * города — значит просто дописать его в `CITY_DISTRICTS`. Для города вне карты шага «район»
 * в интерфейсе нет вовсе — не пустой список, а отсутствие (AGENTS.md, правило 7: пустой
 * список читался бы как «районов нет», а не «деление не заведено»).
 */

/** Город каталога. `id` — латинский slug, он же значение `businesses.city`. */
export interface UzCity {
  readonly id: string;
  readonly nameRu: string;
  readonly nameUz: string;
}

/** Район города. `id` — латинский slug, он же значение `businesses.district`. */
export interface UzDistrict {
  readonly id: string;
  readonly nameRu: string;
  readonly nameUz: string;
}

/**
 * Города Узбекистана, доступные в каталоге. Порядок — по убыванию размера рынка (столица,
 * затем крупные центры): в списке выбора он задаёт порядок строк, а чаще всего нужный город
 * у клиента — сверху.
 */
export const UZ_CITIES: readonly UzCity[] = [
  // Порядок — строго по населению (оценка из базы лидов Google Maps, тыс. чел. в комментарии).
  // Самый людный рынок — сверху: нужный клиенту город обычно в начале списка.
  { id: 'tashkent', nameRu: 'Ташкент', nameUz: 'Toshkent' }, // ~3150
  { id: 'namangan', nameRu: 'Наманган', nameUz: 'Namangan' }, // ~715
  { id: 'samarkand', nameRu: 'Самарканд', nameUz: 'Samarqand' }, // ~600
  { id: 'andijan', nameRu: 'Андижан', nameUz: 'Andijon' }, // ~495
  { id: 'nukus', nameRu: 'Нукус', nameUz: 'Nukus' }, // ~345
  { id: 'fergana', nameRu: 'Фергана', nameUz: 'Fargʻona' }, // ~330
  { id: 'kokand', nameRu: 'Коканд', nameUz: 'Qoʻqon' }, // ~320
  { id: 'qarshi', nameRu: 'Карши', nameUz: 'Qarshi' }, // ~305
  { id: 'bukhara', nameRu: 'Бухара', nameUz: 'Buxoro' }, // ~300
  { id: 'margilan', nameRu: 'Маргилан', nameUz: 'Margʻilon' }, // ~255
  { id: 'termez', nameRu: 'Термез', nameUz: 'Termiz' }, // ~205
  { id: 'jizzakh', nameRu: 'Джизак', nameUz: 'Jizzax' }, // ~203
  { id: 'angren', nameRu: 'Ангрен', nameUz: 'Angren' }, // ~183
  { id: 'chirchiq', nameRu: 'Чирчик', nameUz: 'Chirchiq' }, // ~175
  { id: 'navoiy', nameRu: 'Навои', nameUz: 'Navoiy' }, // ~165
  { id: 'urgench', nameRu: 'Ургенч', nameUz: 'Urganch' }, // ~155
  { id: 'almalyk', nameRu: 'Алмалык', nameUz: 'Olmaliq' }, // ~150
  { id: 'shahrisabz', nameRu: 'Шахрисабз', nameUz: 'Shahrisabz' }, // ~148
  { id: 'bekabad', nameRu: 'Бекабад', nameUz: 'Bekobod' }, // ~105
  { id: 'gulistan', nameRu: 'Гулистан', nameUz: 'Guliston' }, // ~105
  { id: 'khiva', nameRu: 'Хива', nameUz: 'Xiva' }, // ~101
  { id: 'kattakurgan', nameRu: 'Каттакурган', nameUz: 'Kattaqoʻrgʻon' }, // ~97
  { id: 'yangiyul', nameRu: 'Янгиюль', nameUz: 'Yangiyoʻl' }, // ~92
  { id: 'zarafshan', nameRu: 'Зарафшан', nameUz: 'Zarafshon' }, // ~92
  { id: 'nurafshon', nameRu: 'Нурафшон', nameUz: 'Nurafshon' }, // ~59
  { id: 'khanabad', nameRu: 'Ханабад', nameUz: 'Xonobod' }, // ~47
  { id: 'ahangaran', nameRu: 'Ахангаран', nameUz: 'Ohangaron' }, // ~43
  { id: 'uchquduq', nameRu: 'Учкудук', nameUz: 'Uchquduq' }, // ~32
];

/**
 * Районы по городам. Ключ — `UzCity.id`. Заполнены только города с районным делением в UI;
 * сейчас — один Ташкент (12 туманов). Добавление другого города сюда автоматически включает
 * для него шаг выбора района и на фронте, и в фильтре — отдельного кода писать не нужно.
 */
export const CITY_DISTRICTS: Readonly<Record<string, readonly UzDistrict[]>> = {
  tashkent: [
    { id: 'bektemir', nameRu: 'Бектемир', nameUz: 'Bektemir' },
    { id: 'chilonzor', nameRu: 'Чиланзар', nameUz: 'Chilonzor' },
    { id: 'mirobod', nameRu: 'Мирабад', nameUz: 'Mirobod' },
    { id: 'mirzo-ulugbek', nameRu: 'Мирзо-Улугбек', nameUz: 'Mirzo Ulugʻbek' },
    { id: 'olmazor', nameRu: 'Алмазар', nameUz: 'Olmazor' },
    { id: 'sergeli', nameRu: 'Сергели', nameUz: 'Sergeli' },
    { id: 'shayxontohur', nameRu: 'Шайхантахур', nameUz: 'Shayxontohur' },
    { id: 'uchtepa', nameRu: 'Учтепа', nameUz: 'Uchtepa' },
    { id: 'yakkasaroy', nameRu: 'Яккасарай', nameUz: 'Yakkasaroy' },
    { id: 'yangihayot', nameRu: 'Янгихаёт', nameUz: 'Yangihayot' },
    { id: 'yashnobod', nameRu: 'Яшнабад', nameUz: 'Yashnobod' },
    { id: 'yunusobod', nameRu: 'Юнусабад', nameUz: 'Yunusobod' },
  ],
};

const CITY_IDS = new Set(UZ_CITIES.map((c) => c.id));

/** Известен ли город каталогу. Неизвестный — ошибка запроса, а не повод показать всё подряд. */
export function isKnownCity(id: string): boolean {
  return CITY_IDS.has(id);
}

/** Районы города; пустой массив — у города нет районного деления в UI (не ошибка). */
export function districtsOf(cityId: string): readonly UzDistrict[] {
  return CITY_DISTRICTS[cityId] ?? [];
}

/** Принадлежит ли район этому городу. `false` и для неизвестного города, и для чужого района. */
export function isKnownDistrict(cityId: string, districtId: string): boolean {
  return districtsOf(cityId).some((d) => d.id === districtId);
}
